import * as NextSkyTheme from "@NextSkyTheme/global";
import { LazyLoader } from "@NextSkyTheme/lazy-load";
import { eventModal } from "@NextSkyTheme/modal";

const NEWSLETTER_SUCCESS_COOKIE = "newsletter_popup_success";

function hasNewsletterSuccessCookie() {
  return NextSkyTheme.getCookie(NEWSLETTER_SUCCESS_COOKIE) !== null;
}

function getOfferSlide(card) {
  return (
    card.closest(".shopify-block") ||
    card.closest(".swiper-slide") ||
    card
  );
}

function syncNewsletterOfferVisibility(scope = document) {
  const subscribed = hasNewsletterSuccessCookie();
  scope.querySelectorAll('[data-newsletter-offer="true"]').forEach((card) => {
    const slide = getOfferSlide(card);
    if (subscribed) {
      slide.classList.add("hidden-important");
      slide.setAttribute("hidden", "");
      slide.setAttribute("aria-hidden", "true");
    } else {
      slide.classList.remove("hidden-important");
      slide.removeAttribute("hidden");
      slide.removeAttribute("aria-hidden");
    }
  });
  updateVisibleOfferCount(scope);
}

function updateVisibleOfferCount(scope = document) {
  const offerPopup =
    (scope.querySelector && scope.querySelector("offer-popup")) ||
    document.querySelector("offer-popup");
  if (!offerPopup) return;

  const countEl = offerPopup.querySelector("[data-offer-count]");
  if (!countEl) return;

  let visible = 0;
  offerPopup.querySelectorAll(".block__image-with-text-offer").forEach((card) => {
    const slide = getOfferSlide(card);
    if (slide.hasAttribute("hidden") || slide.classList.contains("hidden-important")) {
      return;
    }
    visible += 1;
  });
  countEl.textContent = String(visible);
}

function forceCloseOffersPopup() {
  const offerWrapper = document.querySelector("offer-popup");
  const root = NextSkyTheme.getRoot?.() || document.documentElement;
  if (offerWrapper) {
    offerWrapper.classList.remove("active");
    offerWrapper.classList.add("content-hidden");
    offerWrapper.style.pointerEvents = "none";
  }
  root.classList.remove("open-modal-offer-popup");
  return offerWrapper;
}

function openNewsletterFromOffer() {
  const root = NextSkyTheme.getRoot?.() || document.documentElement;
  forceCloseOffersPopup();

  const newsletter = document.querySelector("newsletter-popup");
  let opened = false;

  if (newsletter) {
    try {
      if (!newsletter.initialized && typeof newsletter.init === "function") {
        newsletter.init();
      }
      if (typeof newsletter.initElements === "function") {
        newsletter.initElements();
      }
      if (typeof newsletter.createPopup === "function") {
        newsletter.createPopup();
        opened = true;
      }
    } catch (error) {
      console.warn("[offers] Failed to open newsletter popup", error);
    }
  }

  const modal =
    newsletter?.modal || document.querySelector("newsletter-modal-popup");

  if (!opened && modal) {
    eventModal(modal, "open", true, null, true);
    opened = true;
  }

  if (modal) {
    modal.classList.add("active");
    modal.classList.remove("content-hidden");
    modal.style.zIndex = "40";
    modal.style.opacity = "1";
    modal.style.visibility = "visible";
    modal.style.pointerEvents = "auto";
  }

  root.classList.add("open-modal");
  // Offer-close cleanup removes open-modal ~300ms later — re-assert after that.
  window.setTimeout(() => {
    root.classList.add("open-modal");
    root.classList.remove("open-modal-offer-popup");
    if (modal) {
      modal.classList.add("active");
      modal.style.zIndex = "40";
      modal.style.opacity = "1";
      modal.style.visibility = "visible";
      modal.style.pointerEvents = "auto";
    }
  }, 360);

  return opened || Boolean(modal);
}

function closeOffersThenOpenNewsletter() {
  const offerWrapper = document.querySelector("offer-popup");
  let opened = false;

  const tryOpen = () => {
    if (opened) return;
    if (openNewsletterFromOffer()) {
      opened = true;
    }
  };

  if (offerWrapper?.classList.contains("active")) {
    const onClosed = (event) => {
      if (event.detail?.modal !== offerWrapper) return;
      document.removeEventListener("modal:closed", onClosed);
      // Let the drawer finish hiding, then open newsletter on top.
      window.setTimeout(tryOpen, 280);
    };
    document.addEventListener("modal:closed", onClosed);
    eventModal(offerWrapper, "close", false);

    // Fallback if modal:closed never matches.
    window.setTimeout(() => {
      document.removeEventListener("modal:closed", onClosed);
      forceCloseOffersPopup();
      tryOpen();
      if (!opened) {
        window.setTimeout(tryOpen, 200);
      }
    }, 600);
    return;
  }

  tryOpen();
  if (!opened) {
    window.setTimeout(tryOpen, 200);
  }
}

function bindNewsletterOfferClicks() {
  if (window.__newsletterOfferClickBound) return;
  window.__newsletterOfferClickBound = true;

  document.addEventListener(
    "click",
    (event) => {
      const card = event.target?.closest?.('[data-open-newsletter="true"]');
      if (!card) return;

      const actionable = event.target?.closest?.("a, button, .btn, .btn-primary, .btn-outline");
      if (!actionable || !card.contains(actionable)) return;
      if (actionable.closest(".modal-button-close")) return;

      event.preventDefault();
      event.stopPropagation();

      if (hasNewsletterSuccessCookie()) {
        syncNewsletterOfferVisibility();
        return;
      }

      closeOffersThenOpenNewsletter();
    },
    true
  );
}

class ScrollOffer extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.observer = null;
    this.scrollThreshold = 300;
    this.initialized = false;
    this.isPopupOpen = false;
    this.initMobile();
  }

  connectedCallback() {
    this.addEventListener("click", this.initPopup.bind(this), false);
    this.addEventListener("keydown", this.handleKeydown.bind(this), false);
    const offerWrapper = document.querySelector("offer-popup");
    this.sectionId = offerWrapper?.dataset.sectionId;
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.initialize());
    } else {
      this.initialize();
    }

    if (window.Shopify && window.Shopify.designMode) {
      const _self = this;
      document.addEventListener("shopify:section:select", (event) => {
        _self.actionDesignMode(event);
      });
      document.addEventListener("shopify:section:deselect", () => {
        _self.closePopup();
      });
    }
  }

  actionDesignMode(event) {
    const _self = this;
    const currentTarget = event.target;
    let editorSectionId = null;
    try {
      editorSectionId = JSON.parse(
        currentTarget.dataset.shopifyEditorSection || "null"
      )?.id;
    } catch {
      return;
    }
    if (!this.sectionId) {
      const offerWrapper = document.querySelector("offer-popup");
      this.sectionId = offerWrapper?.dataset.sectionId;
    }
    if (editorSectionId && editorSectionId === this.sectionId) {
      _self.initPopup();
    } else {
      _self.closePopup();
    }
  }

  initPopup() {
    this.ensureOffersMounted();
    const offerWrapper = document.querySelector("offer-popup");
    if (!offerWrapper) return;
    this.sectionId = offerWrapper.dataset.sectionId || this.sectionId;
    if (window.Shopify && window.Shopify.designMode) {
      eventModal(offerWrapper, "close", false);
    }
    eventModal(offerWrapper, "open", false);
    document.documentElement.classList.add("open-modal-offer-popup");
    NextSkyTheme.global.rootToFocus = this;
    window.setTimeout(() => syncNewsletterOfferVisibility(), 0);
  }

  /** Mount lazy-template offers markup so the first header click works. */
  ensureOffersMounted() {
    if (document.querySelector("offer-popup")) return;
    document.querySelectorAll("template.lazy-template-modal").forEach((el) => {
      if (!el.content?.querySelector?.("offer-popup")) return;
      const section = el.closest(".shopify-section");
      if (!section) return;
      const content = document.createElement("div");
      content.appendChild(el.content.firstElementChild.cloneNode(true));
      const mounted = content.querySelector(".content-element");
      if (mounted) section.appendChild(mounted);
      el.remove();
    });
  }

  closePopup() {
    const offerWrapper = document.querySelector("offer-popup");
    if (!offerWrapper) return;
    eventModal(offerWrapper, "close", false);
    this.isPopupOpen = false;
  }

  initMobile() {
    const mediaQuery = window.matchMedia("(max-width: 767.98px)");
    const _self = this;

    let timeoutId = null;

    const handleMediaQueryChange = () => {
      if (mediaQuery.matches) {
        const mobileNavigationBar = document.querySelector(
          "mobile-navigation-bar"
        );
        if (
          mobileNavigationBar &&
          mobileNavigationBar.classList.contains("show_offer")
        ) {
          _self.mobileAppend();
        }
      }
    };

    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleMediaQueryChange, 300);
    };

    handleMediaQueryChange();
    mediaQuery.addEventListener("change", handleMediaQueryChange);

    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;

      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          shouldCheck = true;
        }
        if (mutation.type === "childList") {
          shouldCheck = true;
        }
      });

      if (shouldCheck) {
        debouncedCheck();
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
      childList: true,
      subtree: true,
    });

    if (typeof Shopify !== "undefined" && Shopify.designMode) {
      [
        "shopify:section:load",
        "shopify:section:reorder",
        "shopify:section:unload",
      ].forEach((event) => {
        document.addEventListener(event, debouncedCheck);
      });
    }
  }

  mobileAppend() {
    const mobileNavigationBar = document.querySelector("mobile-navigation-bar");

    if (!mobileNavigationBar) {
      return;
    }
    const mobileNavigationBarContent = mobileNavigationBar.querySelector(
      ".mobile-navigation-bar__content"
    );
    if (!mobileNavigationBarContent) return;

    const existingOfferMobile =
      mobileNavigationBarContent.querySelector(".offer-mobile");
    if (existingOfferMobile) {
      return;
    }
    const contentAppend = document.createElement("li");
    contentAppend.classList.add("flex-1", "offer-mobile");
    const offerTitle = window.content?.offer?.title || "Offers";
    contentAppend.innerHTML = `
      <div class="mobile_icon pointer inline-flex column content-center heading-color gap-3">
<svg width="20" height="20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.326 12.217 2.059 10.95a1.338 1.338 0 0 1 0-1.883L3.326 7.8c.217-.216.392-.641.392-.941V5.067c0-.733.6-1.333 1.333-1.333h1.792c.3 0 .725-.175.941-.392l1.267-1.267a1.338 1.338 0 0 1 1.883 0l1.267 1.267c.217.217.642.392.942.392h1.791c.734 0 1.334.6 1.334 1.333V6.86c0 .3.175.725.391.941l1.267 1.267a1.338 1.338 0 0 1 0 1.883l-1.267 1.267c-.216.217-.391.642-.391.942v1.791c0 .734-.6 1.334-1.334 1.334h-1.791c-.3 0-.725.175-.942.392l-1.267 1.266a1.338 1.338 0 0 1-1.883 0l-1.267-1.267a1.537 1.537 0 0 0-.941-.391H5.05c-.733 0-1.333-.6-1.333-1.334V13.16c0-.309-.175-.734-.392-.942ZM7.5 12.5l5-5"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12.08 12.083h.008M7.912 7.917h.008"/></svg>
        ${offerTitle}
      </div>
    `;
    const lastChild = mobileNavigationBarContent.lastElementChild;
    if (lastChild) {
      mobileNavigationBarContent.insertBefore(contentAppend, lastChild);
    } else {
      mobileNavigationBarContent.appendChild(contentAppend);
    }
    contentAppend.addEventListener("click", this.initPopup.bind(this), false);
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.initPopup();
    }
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;
    bindNewsletterOfferClicks();
    syncNewsletterOfferVisibility();
    document.addEventListener(
      "modal:opened",
      this.handleModalOpened.bind(this)
    );
  }

  handleModalOpened() {
    if (this.tagName === "SCROLL-OFFER") {
      new LazyLoader(".image-lazy-load");
      syncNewsletterOfferVisibility();
    }
  }
}

if (!customElements.get("scroll-offer")) {
  customElements.define("scroll-offer", ScrollOffer);
}

bindNewsletterOfferClicks();

const syncVisibilitySoon = () => syncNewsletterOfferVisibility();
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", syncVisibilitySoon);
} else {
  syncVisibilitySoon();
}
// Offers markup is lazy-mounted after first interaction.
window.addEventListener("nextskyspeed-allScriptsLoaded", syncVisibilitySoon);
document.addEventListener("modal:opened", syncVisibilitySoon);
